import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import {
  MapPin, Calendar, PlayCircle, CheckCircle2, Clock, ClipboardList,
  DollarSign, Home, Camera, X, Wifi, Key, AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { PhotoLightbox, useLightbox } from "@/components/photo-lightbox";

type GeneralTask = {
  id: number;
  title: string;
  description: string | null;
  status: string;
  taskType: string;
  scheduledAt: string | null;
  address: string | null;
  apartmentId: number | null;
  apartmentName: string | null;
  apartmentAddress: string | null;
  apartmentLocation: string | null;
  fobInstructions: string | null;
  wifiSsid: string | null;
  wifiPassword: string | null;
  billable: boolean;
  billingAmount: string | null;
  completionNotes: string | null;
};

type TaskPhoto = { id: number; url: string; caption: string | null; createdAt: string };

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  pending:     { label: "Pending",     variant: "secondary" },
  in_progress: { label: "In Progress", variant: "default" },
  completed:   { label: "Completed",   variant: "secondary" },
};

const taskTypeLabels: Record<string, string> = {
  checkin:    "Check-in",
  checkout:   "Check-out",
  unit_setup: "Unit Setup",
  inspection: "Inspection",
  laundry:    "Laundry",
  other:      "General Task",
};

export function PropertyContext({ task }: { task: GeneralTask }) {
  // Show apartment/property identity FIRST and prominently — this is the
  // primary fix for FEAT-041: cleaners must know where to go before doing
  // anything else.
  if (task.apartmentId) {
    return (
      <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <Home className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm text-foreground" data-testid="text-property-name">
              {task.apartmentName ?? `Unit #${task.apartmentId}`}
            </p>
            {task.apartmentAddress && (
              <p className="text-xs text-muted-foreground break-words" data-testid="text-property-address">
                {task.apartmentAddress}
                {task.apartmentLocation ? ` · ${task.apartmentLocation}` : ""}
              </p>
            )}
            {!task.apartmentAddress && task.apartmentLocation && (
              <p className="text-xs text-muted-foreground" data-testid="text-property-location">
                {task.apartmentLocation}
              </p>
            )}
          </div>
        </div>
        {(task.fobInstructions || task.wifiSsid) && (
          <div className="space-y-1 pt-1 border-t border-primary/20">
            {task.fobInstructions && (
              <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                <Key className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="break-words" data-testid="text-property-fob">{task.fobInstructions}</span>
              </div>
            )}
            {task.wifiSsid && (
              <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                <Wifi className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  <span className="font-medium">{task.wifiSsid}</span>
                  {task.wifiPassword ? ` · ${task.wifiPassword}` : ""}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  if (task.address) {
    return (
      <div className="rounded-md border bg-muted/30 p-2.5 flex items-start gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium" data-testid="text-task-address">{task.address}</p>
          <p className="text-[11px] text-muted-foreground">External location</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-destructive/50 bg-destructive/5 p-2.5 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
      <p className="text-xs text-destructive">No property or address set — ask your admin where to go.</p>
    </div>
  );
}

export function CompleteTaskDialog({
  task, endpoint, open, onOpenChange, onCompleted,
}: {
  task: GeneralTask;
  endpoint: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCompleted: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const { lightboxState, openLightbox, closeLightbox } = useLightbox();

  const { data: photos = [] } = useQuery<TaskPhoto[]>({
    queryKey: [`${endpoint}/${task.id}/photos`],
    enabled: open,
  });

  const uploadPhotos = useMutation({
    mutationFn: async (files: File[]) => {
      setUploading(true);
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${endpoint}/${task.id}/photos`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Photo upload failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${endpoint}/${task.id}/photos`] });
      setUploading(false);
    },
    onError: (err: Error) => {
      setUploading(false);
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  const deletePhoto = useMutation({
    mutationFn: async (photoId: number) => {
      const res = await fetch(`${endpoint}/${task.id}/photos/${photoId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${endpoint}/${task.id}/photos`] });
    },
  });

  const completeTask = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `${endpoint}/${task.id}`, {
        status: "completed",
        completionNotes: notes.trim() || null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      toast({ title: "Task completed", description: `${photos.length} photo(s) attached` });
      onCompleted();
      onOpenChange(false);
      setNotes("");
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const lightboxPhotos = photos.map(p => ({ id: p.id, url: p.url, label: p.caption || "Photo" }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete: {task.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <PropertyContext task={task} />

          <div>
            <Label className="text-sm font-medium flex items-center gap-1.5 mb-2">
              <Camera className="w-4 h-4" /> Completion photos
              {photos.length > 0 && <Badge variant="secondary" className="text-[10px]">{photos.length}</Badge>}
            </Label>
            <label className="block">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) uploadPhotos.mutate(files);
                  e.target.value = "";
                }}
                disabled={uploading}
                data-testid={`input-task-photo-upload-${task.id}`}
              />
              <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-primary px-3 py-2 text-sm text-primary hover:bg-primary/5">
                <Camera className="w-4 h-4" />
                {uploading ? "Uploading..." : "Take / Upload photos"}
              </span>
            </label>
            {photos.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {photos.map((p, idx) => (
                  <div key={p.id} className="relative group">
                    <img
                      src={p.url}
                      alt="completion proof"
                      className="w-20 h-20 object-cover rounded cursor-pointer"
                      onClick={() => openLightbox(lightboxPhotos, idx)}
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100"
                      onClick={() => deletePhoto.mutate(p.id)}
                      data-testid={`button-delete-task-photo-${p.id}`}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              Photos prove the task was done at the right property. Required for billable tasks.
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the admin should know about this task?"
              rows={3}
              data-testid={`input-task-completion-notes-${task.id}`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => completeTask.mutate()}
            disabled={completeTask.isPending || uploading || (task.billable && photos.length === 0)}
            data-testid={`button-submit-task-completion-${task.id}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {task.billable && photos.length === 0
              ? "Photo required"
              : completeTask.isPending ? "Saving..." : "Complete task"}
          </Button>
        </DialogFooter>
        {lightboxState && (
          <PhotoLightbox photos={lightboxState.photos} initialIndex={lightboxState.index} onClose={closeLightbox} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TaskCard({
  task, endpoint, onStatusChange,
}: {
  task: GeneralTask;
  endpoint: string;
  onStatusChange: (id: number, status: string) => void;
}) {
  const cfg = statusConfig[task.status] ?? { label: task.status, variant: "outline" as const };
  const [completeOpen, setCompleteOpen] = useState(false);

  return (
    <Card className="border border-border/50">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm leading-tight" data-testid={`text-task-title-${task.id}`}>{task.title}</h3>
          <Badge variant={cfg.variant} className="shrink-0">{cfg.label}</Badge>
        </div>

        <PropertyContext task={task} />

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">{taskTypeLabels[task.taskType] ?? task.taskType}</Badge>
          {task.billable && task.billingAmount && (
            <span className="flex items-center gap-0.5 text-xs text-emerald-600 font-medium">
              <DollarSign className="w-3 h-3" />{task.billingAmount}
            </span>
          )}
          {task.scheduledAt && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              {format(new Date(task.scheduledAt), "MMM d · h:mm a")}
            </span>
          )}
        </div>

        {task.description && (
          <p className="text-sm text-muted-foreground border-t pt-2 whitespace-pre-line">{task.description}</p>
        )}

        {task.status !== "completed" && (
          <div className="flex gap-2 pt-1">
            {task.status === "pending" && (
              <Button
                size="sm"
                className="flex-1"
                onClick={() => onStatusChange(task.id, "in_progress")}
                data-testid={`button-start-task-${task.id}`}
              >
                <PlayCircle className="w-4 h-4 mr-1.5" /> Start Task
              </Button>
            )}
            {task.status === "in_progress" && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => setCompleteOpen(true)}
                data-testid={`button-open-complete-task-${task.id}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete with proof
              </Button>
            )}
          </div>
        )}
        {task.status === "completed" && (
          <div className="flex items-center gap-1.5 text-xs text-green-600">
            <CheckCircle2 className="w-3.5 h-3.5" /> Completed
          </div>
        )}

        <CompleteTaskDialog
          task={task}
          endpoint={endpoint}
          open={completeOpen}
          onOpenChange={setCompleteOpen}
          onCompleted={() => {}}
        />
      </CardContent>
    </Card>
  );
}

export default function WorkerTasksTab({ tasksEndpoint }: { tasksEndpoint: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery<GeneralTask[]>({
    queryKey: [tasksEndpoint],
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiRequest("PUT", `${tasksEndpoint}/${id}`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tasksEndpoint] });
      toast({ title: "Task updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const active = tasks.filter(t => t.status !== "completed");
  const done   = tasks.filter(t => t.status === "completed");

  if (isLoading) return (
    <div className="space-y-3">
      {[1, 2].map(i => <Skeleton key={i} className="h-32 w-full" />)}
    </div>
  );

  if (!tasks.length) return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
      <ClipboardList className="w-10 h-10 opacity-30" />
      <p className="text-sm">No tasks assigned to you.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {active.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Clock className="w-4 h-4" /> Active ({active.length})
          </div>
          {active.map(t => (
            <TaskCard
              key={t.id}
              task={t}
              endpoint={tasksEndpoint}
              onStatusChange={(id, status) => updateMutation.mutate({ id, status })}
            />
          ))}
        </section>
      )}
      {done.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <CheckCircle2 className="w-4 h-4" /> Done ({done.length})
          </div>
          {done.map(t => (
            <TaskCard
              key={t.id}
              task={t}
              endpoint={tasksEndpoint}
              onStatusChange={(id, status) => updateMutation.mutate({ id, status })}
            />
          ))}
        </section>
      )}
    </div>
  );
}

